import React, { useEffect, useMemo, useState } from 'react';
import { api, fullName, formatDate } from '../lib/api';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { ClipboardCheck, Plus, FileText, Trash2, Search, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import { normalizaTexto } from '../lib/utils';
import PdfPreviewDialog from '../components/PdfPreviewDialog';
import { PatientCombobox } from '../components/PatientCombobox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function JustificationDialog({ open, onOpenChange, initial, patients, onSaved, defaultType }) {
  const isEdit = !!initial?.id;
  const empty = {
    patient_id: '',
    type: defaultType || 'consulta',
    event_date: new Date().toISOString().slice(0, 10),
    time_from: '',
    time_to: '',
    procedure: '',
    rest_days: '',
    companion_name: '',
    additional_notes: '',
    custom_body: '',
  };
  const [form, setForm] = useState(empty);
  const [advanced, setAdvanced] = useState(false);

  useEffect(() => {
    if (open) {
      if (initial) setForm({ ...empty, ...initial, rest_days: initial.rest_days || '' });
      else setForm({ ...empty, type: defaultType || 'consulta' });
      setAdvanced(!!initial?.custom_body);
    }
    // eslint-disable-next-line
  }, [open, initial, defaultType]);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isQX = form.type === 'quirofano';

  const submit = async (e) => {
    e.preventDefault();
    if (!form.patient_id) return toast.error('Selecciona un paciente');
    if (!form.event_date) return toast.error('Indica la fecha');
    try {
      const payload = { ...form };
      // Sanear: enviar números/strings vacíos como null
      payload.rest_days = payload.rest_days ? parseInt(payload.rest_days, 10) : null;
      ['time_from', 'time_to', 'procedure', 'companion_name', 'additional_notes', 'custom_body'].forEach(k => {
        if (payload[k] === '') payload[k] = null;
      });
      if (isEdit) {
        await api.put(`/api/justificantes/${initial.id}`, payload);
        toast.success('Justificante actualizado');
      } else {
        await api.post('/api/justificantes', payload);
        toast.success('Justificante creado');
      }
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Error';
      toast.error(`Error: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEdit ? 'Editar justificante' : 'Nuevo justificante'}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Paciente *</Label>
            <PatientCombobox
              value={form.patient_id}
              onChange={(v) => setF('patient_id', v)}
              patients={patients}
              disabled={isEdit}
              placeholder="Seleccionar paciente…"
              dataTestid="just-patient"
            />
          </div>

          <div>
            <Label>Tipo *</Label>
            <Select value={form.type} onValueChange={(v) => setF('type', v)} disabled={isEdit}>
              <SelectTrigger data-testid="just-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="consulta">🩺 Justificante de consulta</SelectItem>
                <SelectItem value="quirofano">✂️ Justificante de quirófano (intervención)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-1">
              <Label>Fecha del evento *</Label>
              <Input type="date" required value={form.event_date} onChange={(e) => setF('event_date', e.target.value)} data-testid="just-event-date" />
            </div>
            {!isQX && (
              <>
                <div>
                  <Label>Hora desde</Label>
                  <Input type="time" value={form.time_from || ''} onChange={(e) => setF('time_from', e.target.value)} />
                </div>
                <div>
                  <Label>Hora hasta</Label>
                  <Input type="time" value={form.time_to || ''} onChange={(e) => setF('time_to', e.target.value)} />
                </div>
              </>
            )}
            {isQX && (
              <div className="sm:col-span-2">
                <Label>Procedimiento</Label>
                <Input value={form.procedure || ''} onChange={(e) => setF('procedure', e.target.value)} placeholder="Procedimiento realizado" data-testid="just-procedure" />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Días de reposo recomendados</Label>
              <Input type="number" min={0} value={form.rest_days || ''} onChange={(e) => setF('rest_days', e.target.value)} placeholder={isQX ? '10' : '1'} />
            </div>
            <div>
              <Label>Acompañante (opcional)</Label>
              <Input value={form.companion_name || ''} onChange={(e) => setF('companion_name', e.target.value)} placeholder="Nombre del acompañante" />
            </div>
          </div>

          <div>
            <Label>Notas adicionales (opcional)</Label>
            <Textarea rows={2} value={form.additional_notes || ''} onChange={(e) => setF('additional_notes', e.target.value)} placeholder="Texto que se añade al final del cuerpo del justificante" />
          </div>

          <div className="border-t border-border pt-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer text-[#13231E]">
              <input type="checkbox" checked={advanced} onChange={(e) => { setAdvanced(e.target.checked); if (!e.target.checked) setF('custom_body', ''); }} />
              Reemplazar todo el cuerpo del justificante con texto personalizado
            </label>
            {advanced && (
              <Textarea
                rows={6}
                value={form.custom_body || ''}
                onChange={(e) => setF('custom_body', e.target.value)}
                placeholder="Escribe aquí el texto completo del justificante. Sustituye al texto generado automáticamente."
                className="mt-2"
                data-testid="just-custom-body"
              />
            )}
          </div>

          <div className="text-xs text-muted-foreground bg-[rgba(46,110,94,0.08)] p-2 rounded">
            ℹ️ El justificante incluye automáticamente: cabecera de la clínica, datos del paciente y firma del doctor. Puedes editar el contenido tantas veces como necesites antes de imprimir.
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="just-save">
              {isEdit ? <><Edit2 className="w-4 h-4 mr-2" />Guardar</> : <><Plus className="w-4 h-4 mr-2" />Crear</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function JustificationsPage() {
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [tab, setTab] = useState('all');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [defaultType, setDefaultType] = useState('consulta');
  const [preview, setPreview] = useState({ open: false, url: '', filename: '', title: '' });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get('/api/justificantes'),
        api.get('/api/patients'),
      ]);
      setItems(r1.data);
      setPatients(r2.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const visible = useMemo(() => {
    let arr = items;
    if (tab !== 'all') arr = arr.filter(it => it.type === tab);
    if (q) {
      const qq = normalizaTexto(q);
      arr = arr.filter(it => normalizaTexto(it.patient_name).includes(qq) || normalizaTexto(it.procedure).includes(qq));
    }
    return arr;
  }, [items, q, tab]);

  const onDelete = async (id) => {
    if (!window.confirm('¿Eliminar justificante?')) return;
    await api.delete(`/api/justificantes/${id}`);
    toast.success('Eliminado');
    fetchAll();
  };

  const openPdf = (j) => {
    const isQX = j.type === 'quirofano';
    setPreview({
      open: true,
      url: `${BACKEND_URL}/api/justificantes/${j.id}/pdf`,
      filename: `justificante_${isQX ? 'quirofano' : 'consulta'}_${j.patient_name}.pdf`,
      title: `${isQX ? 'Justificante quirófano' : 'Justificante consulta'} · ${j.patient_name}`,
    });
  };

  const openNew = (type) => {
    setEditing(null);
    setDefaultType(type);
    setOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Justificantes"
        subtitle="Documentos de asistencia a consulta y de intervención quirúrgica, editables antes de imprimir."
        actions={
          <>
            <Button variant="outline" onClick={() => openNew('consulta')} data-testid="just-new-consulta">
              🩺 Consulta
            </Button>
            <Button onClick={() => openNew('quirofano')} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="just-new-quirofano">
              ✂️ Quirófano
            </Button>
          </>
        }
      />

      <div className="cp-card p-4 mb-3 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Buscar paciente o procedimiento…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="just-search" />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="just-tab-all">Todos ({items.length})</TabsTrigger>
          <TabsTrigger value="consulta" data-testid="just-tab-consulta">Consulta ({items.filter(i => i.type === 'consulta').length})</TabsTrigger>
          <TabsTrigger value="quirofano" data-testid="just-tab-quirofano">Quirófano ({items.filter(i => i.type === 'quirofano').length})</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4">
          <div className="cp-card overflow-hidden">
            {loading ? <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
              : visible.length === 0
              ? <EmptyState icon={ClipboardCheck} title="Sin justificantes" description="Crea el primero para tu paciente." />
              : <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead className="bg-[rgba(46,110,94,0.04)] text-left">
                  <tr>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Tipo</th>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Paciente</th>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Fecha evento</th>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Detalle</th>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Emitido</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map(j => {
                    const isQX = j.type === 'quirofano';
                    return (
                      <tr key={j.id} className="border-t border-border hover:bg-[rgba(46,110,94,0.03)]" data-testid="just-row">
                        <td className="py-3 px-4">
                          <Badge variant="secondary" className={isQX ? 'bg-[rgba(46,110,94,0.10)] text-[#13231E] border-[rgba(46,110,94,0.25)]' : 'bg-[rgba(46,110,94,0.15)] text-[#13231E] border-[rgba(46,110,94,0.30)]'}>
                            {isQX ? '✂️ Quirófano' : '🩺 Consulta'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 font-medium">{j.patient_name}</td>
                        <td className="py-3 px-4 text-xs">{formatDate(j.event_date)}</td>
                        <td className="py-3 px-4 text-xs text-muted-foreground">
                          {isQX
                            ? (j.procedure || '—') + (j.rest_days ? ` · ${j.rest_days}d reposo` : '')
                            : (j.time_from && j.time_to ? `${j.time_from} – ${j.time_to}` : '—')
                          }
                        </td>
                        <td className="py-3 px-4 text-xs">{formatDate(j.issue_date || j.created_at)}</td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex gap-1 items-center justify-end">
                            <Button size="sm" variant="outline" onClick={() => openPdf(j)} title="Ver / Imprimir" data-testid="just-pdf-button">
                              <FileText className="w-4 h-4 mr-1" /> PDF
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { setEditing(j); setOpen(true); }}>Editar</Button>
                            <Button size="sm" variant="ghost" onClick={() => onDelete(j.id)} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table></div>
            }
          </div>
        </TabsContent>
      </Tabs>

      <JustificationDialog
        open={open}
        onOpenChange={setOpen}
        initial={editing}
        patients={patients}
        defaultType={defaultType}
        onSaved={fetchAll}
      />
      <PdfPreviewDialog
        open={preview.open}
        onOpenChange={(v) => setPreview(s => ({ ...s, open: v }))}
        url={preview.url}
        filename={preview.filename}
        title={preview.title}
      />
    </div>
  );
}
